import { Component, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const IN_STOCK = 'IN_STOCK';
const LOW_STOCK = 'LOW_STOCK';

interface VariantStock {
    available: boolean;
    inventory: number;
    store: string;
    warehouse: string;
}

@Component
export class StockStatus extends ThemeComponent {
    @Ref dispatch!: HTMLElement;
    @Ref dispatchTitle!: HTMLElement;
    @Ref dispatchText!: HTMLElement;
    @Ref pickup!: HTMLElement;
    @Ref pickupText!: HTMLElement;
    @Ref outOfStock!: HTMLElement;

    private activeItemClass: string = 'product-stock-status-item--active';
    private lowItemClass: string = 'product-stock-status-item--low';

    private variants: Record<string, VariantStock> = {};

    private isPreorder: boolean = false;
    private isPickupOnly: boolean = false;
    private showPickup: boolean = false;
    private deliveryDays: number[] = [];
    private cutoffTime: string = '12:00';
    private text: Record<string, string> = {};

    init() {
        this.readConfig();
        this.render(this.getAttribute('variant-id') || '');

        const productWrap = this.closest('.product-wrap-js');
        if (productWrap) {
            const productOptionPicker = productWrap.querySelector('product-option-picker');
            if (productOptionPicker && productOptionPicker.tagName === 'PRODUCT-OPTION-PICKER') {
                const productOptionPickerComponent = productOptionPicker as ProductOptionPicker;
                // eslint-disable-next-line max-len
                this.$subscribe(productOptionPickerComponent.onSelectedVariantChange, (variant: Shopify.Liquid.Variant|null) => {
                    this.render(variant ? String(variant.id) : '');
                });
            }
        }
    }

    private readConfig() {
        try {
            this.variants = JSON.parse(this.getAttribute('variant-stock') || '{}');
        } catch (_) {
            this.variants = {};
        }

        this.isPreorder = this.getAttribute('is-preorder') === 'true';
        this.isPickupOnly = this.getAttribute('is-pickup-only') === 'true';
        this.showPickup = this.getAttribute('show-pickup') === 'true';
        this.cutoffTime = this.getAttribute('cutoff-time') || '12:00';
        this.deliveryDays = (this.getAttribute('delivery-days') || '')
            .split(',')
            .map((day) => parseInt(day, 10))
            .filter((day) => !isNaN(day));

        for (const name of [
            'dispatch-title', 'dispatch-text', 'low-title', 'low-text', 'pickup-only-text',
            'preorder-title', 'preorder-text', 'pickup-ready-text', 'pickup-low-text', 'pickup-transit-text',
        ]) {
            this.text[name] = this.getAttribute(name) || '';
        }
    }

    render(variantId: string) {
        const variant = this.variants[variantId];

        if (!variant || !variant.available) {
            this.showOutOfStock();
            return;
        }

        this.toggleClass(this.outOfStock, this.activeItemClass, false);
        this.toggleClass(this.dispatch, this.activeItemClass, true);

        if (this.isPreorder && variant.inventory <= 0) {
            this.setDispatch(this.text['preorder-title'], this.text['preorder-text'], false);
            this.toggleClass(this.pickup, this.activeItemClass, false);
            return;
        }

        const hasStockData = variant.store !== '' || variant.warehouse !== '';
        const anyInStock = variant.store === IN_STOCK || variant.warehouse === IN_STOCK;
        const isLow = hasStockData && !anyInStock
            && (variant.store === LOW_STOCK || variant.warehouse === LOW_STOCK);

        if (this.isPickupOnly) {
            this.setDispatch(this.text['dispatch-title'], this.text['pickup-only-text'], false);
        } else if (isLow) {
            this.setDispatch(this.text['low-title'], this.text['low-text'], true);
        } else {
            this.setDispatch(this.text['dispatch-title'], this.text['dispatch-text'], false);
        }

        this.renderPickup(variant, hasStockData);
    }

    private renderPickup(variant: VariantStock, hasStockData: boolean) {
        const storeHasStock = variant.store === IN_STOCK || variant.store === LOW_STOCK;
        const warehouseHasStock = variant.warehouse === IN_STOCK || variant.warehouse === LOW_STOCK;

        if (!this.showPickup || !hasStockData || (!storeHasStock && !warehouseHasStock)) {
            this.toggleClass(this.pickup, this.activeItemClass, false);
            return;
        }

        if (variant.store === IN_STOCK) {
            this.pickupText.textContent = this.text['pickup-ready-text'];
        } else if (variant.store === LOW_STOCK) {
            this.pickupText.textContent = this.text['pickup-low-text'];
        } else {
            this.pickupText.textContent = this.buildTransitText();
        }

        this.toggleClass(this.pickup, this.lowItemClass, variant.store === LOW_STOCK);
        this.toggleClass(this.pickup, this.activeItemClass, true);
    }

    private setDispatch(title: string, text: string, isLow: boolean) {
        this.dispatchTitle.textContent = title;
        this.dispatchText.textContent = text;
        this.toggleClass(this.dispatch, this.lowItemClass, isLow);
    }

    private buildTransitText(): string {
        const day = this.nearestDeliveryDay();

        if (!day) {
            return this.text['pickup-ready-text'];
        }

        return this.text['pickup-transit-text']
            .replace('#day#', day)
            .replace('#time#', this.cutoffTime);
    }

    private nearestDeliveryDay(): string {
        if (this.deliveryDays.length === 0) {return '';}

        const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
        const today = now.getDay();
        const cutoff = this.cutoffTime.split(':');
        const cutoffHour = parseInt(cutoff[0], 10) || 0;
        const cutoffMinute = parseInt(cutoff[1], 10) || 0;

        for (let offset = 0; offset < 7; offset++) {
            const dayIndex = (today + offset) % 7;
            if (this.deliveryDays.indexOf(dayIndex) === -1) {continue;}

            if (offset === 0) {
                const pastCutoff = now.getHours() > cutoffHour
                    || (now.getHours() === cutoffHour && now.getMinutes() >= cutoffMinute);
                if (pastCutoff) {continue;}
            }

            return DAY_NAMES[dayIndex];
        }

        return '';
    }

    showOutOfStock() {
        this.toggleClass(this.dispatch, this.activeItemClass, false);
        this.toggleClass(this.pickup, this.activeItemClass, false);
        this.toggleClass(this.outOfStock, this.activeItemClass, true);
    }

    private toggleClass(item: HTMLElement, className: string, active: boolean) {
        if (!item) {return;}
        if (active) {
            item.classList.add(className);
        } else {
            item.classList.remove(className);
        }
    }
}
